import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { DatabaseBackup, Download, ShieldCheck, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/common";
import { fmtDT } from "@/lib/format";
import { DemoPurgeCard } from "@/components/DemoPurgeCard";

const TONE = { completato: "bg-emerald-100 text-emerald-800", fallito: "bg-red-100 text-red-700", in_corso: "bg-amber-100 text-amber-800", scaduto: "bg-zinc-200 text-zinc-600" };

export default function Admin() {
  const { user } = useAuth();
  const isCeo = user.role === "ceo";
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [restore, setRestore] = useState(null);
  const [confirm, setConfirm] = useState("");
  const load = useCallback(() => {
    api.get("/admin/backups").then((r) => setRows(r.data));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const wrap = (fn, ok) => async () => {
    setBusy(true);
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const exportAll = async () => {
    const r = await api.get("/admin/export", { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `drapp-export-${new Date().toISOString().slice(0, 10)}.zip`;
    a.click();
  };
  const verify = (b) => wrap(async () => {
    const { data } = await api.post(`/admin/backups/${b.id}/verify`);
    if (!data.ok) throw new Error(data.error || "Verifica fallita");
  }, "Backup verificato: decifrato e integro")();
  const doRestore = wrap(async () => {
    await api.post(`/admin/backups/${restore.id}/restore`, { confirm });
    setRestore(null);
    setConfirm("");
  }, "Ripristino completato (creato prima un backup di sicurezza)");

  return (
    <div data-testid="admin-page">
      <PageHeader title="Backup e dati" subtitle="Backup cifrati (AES via Fernet) su archivio separato dal database, verificabili e ripristinabili"
        actions={<>
          <Button variant="outline" onClick={wrap(exportAll, "Export scaricato")} disabled={busy} className="h-10 rounded-xl" data-testid="admin-export-button"><Download className="mr-1 h-4 w-4" />Export completo (ZIP)</Button>
          {isCeo && <Button onClick={wrap(() => api.post("/admin/backups"), "Backup creato")} disabled={busy} className="h-10 rounded-xl font-bold" data-testid="admin-backup-now-button"><DatabaseBackup className="mr-1 h-4 w-4" />Backup ora</Button>}
        </>} />
      {isCeo && <DemoPurgeCard />}
      <div className="mb-5 grid gap-3 md:grid-cols-3">
        <div className="card-soft p-4 text-sm"><b>Automatico</b><p className="text-muted-foreground">Ogni notte alle 02:30 (ora italiana), seguito da verifica automatica di decifratura e conteggi.</p></div>
        <div className="card-soft p-4 text-sm"><b>Conservazione</b><p className="text-muted-foreground">Ultimi 30 backup completati. I precedenti sono marcati come scaduti.</p></div>
        <div className="card-soft p-4 text-sm"><b>Ripristino</b><p className="text-muted-foreground">Solo CEO, con conferma testuale. Prima del ripristino viene creato un backup di sicurezza.</p></div>
      </div>
      {rows.length === 0 && <Empty text="Nessun backup ancora eseguito." />}
      <div className="card-soft overflow-x-auto">
        <table className="w-full min-w-[760px] text-sm" data-testid="admin-backups-table">
          <thead className="bg-muted/60 text-left text-xs uppercase text-muted-foreground"><tr><th className="px-4 py-3">Data</th><th>Origine</th><th>Dimensione</th><th>Stato</th><th>Verifica</th><th></th></tr></thead>
          <tbody>{rows.map((b) => (
            <tr key={b.id} className="border-t" data-testid={`backup-row-${b.id}`}>
              <td className="px-4 py-2">{fmtDT(b.created_at)}</td><td>{b.trigger}</td><td>{(b.size / 1024).toFixed(1)} KB</td>
              <td><span className={`rounded-full px-2 py-0.5 text-xs font-bold ${TONE[b.status]}`}>{b.status}</span></td>
              <td>{b.verified_at ? <span className={b.verify_ok ? "text-emerald-700" : "text-red-600"}>{b.verify_ok ? "OK" : "Errore"} · {fmtDT(b.verified_at)}</span> : "—"}</td>
              <td className="space-x-2 pr-4 text-right">{isCeo && b.status === "completato" && <>
                <Button size="sm" variant="outline" className="rounded-xl" onClick={() => verify(b)} disabled={busy} data-testid={`backup-verify-${b.id}`}><ShieldCheck className="mr-1 h-3 w-3" />Verifica</Button>
                <Button size="sm" variant="destructive" className="rounded-xl" onClick={() => setRestore(b)} data-testid={`backup-restore-${b.id}`}><RotateCcw className="mr-1 h-3 w-3" />Ripristina</Button></>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>
      <Dialog open={!!restore} onOpenChange={(o) => !o && setRestore(null)}>
        <DialogContent className="rounded-2xl" data-testid="restore-dialog">
          <DialogHeader><DialogTitle>Ripristino del database</DialogTitle>
            <DialogDescription>Tutti i dati attuali saranno sostituiti con quelli del backup del {fmtDT(restore?.created_at)}. Digita RIPRISTINA per confermare.</DialogDescription></DialogHeader>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="restore-confirm-input" />
          <DialogFooter><Button variant="destructive" disabled={confirm !== "RIPRISTINA" || busy} onClick={doRestore} className="rounded-xl" data-testid="restore-confirm-button">Ripristina</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
